'use client';

import { NODE_ENV } from '@/constants/environment';
import {
  useEffect,
  useMemo,
  useState,
  createContext,
  useContext,
  ReactNode,
  useRef,
} from 'react';
import { LDProvider, useLDClient } from 'launchdarkly-react-client-sdk';
import type * as LDClient from 'launchdarkly-js-client-sdk';
import { datadogRum } from '@datadog/browser-rum';

// LaunchDarkly context interface
export interface LaunchDarklyContext {
  kind: string;
  key: string;
  anonymous: boolean;
  email?: string;
  name?: string;
  custom: {
    environment: string;
    isProduction: boolean;
    host: string;
    timestamp: string;
    userId?: number;
    userGuid?: string;
    organizationId?: number;
    userType?: string;
  };
}

type UserInfo = {
  userId?: number;
  userEmail?: string;
  userName?: string;
  userGuid?: string;
  organizationId?: number;
  userType?: string;
};

interface LaunchDarklyUpdateContext {
  updateUser: (userInfo: UserInfo) => void;
  getCurrentContext: () => LaunchDarklyContext;
}

const LaunchDarklyUpdateContext =
  createContext<LaunchDarklyUpdateContext | null>(null);

export const useLaunchDarklyUpdate = () => {
  const context = useContext(LaunchDarklyUpdateContext);
  if (!context) {
    throw new Error(
      'useLaunchDarklyUpdate must be used within LaunchDarklyProvider',
    );
  }
  return context;
};

interface LaunchDarklyProviderProps {
  children: ReactNode;
}

// Calls ldClient.identify() whenever the user context changes. Kept as a
// separate child of LDProvider (uses useLDClient) so the LDProvider tree is
// stable from the first render — React doesn't remount the app when the SDK
// becomes available.
function LaunchDarklyIdentifySync({
  userInfo,
  userContext,
}: {
  userInfo: UserInfo;
  userContext: LaunchDarklyContext;
}) {
  const ldClient = useLDClient();
  const prevUserInfoRef = useRef<UserInfo>({});

  useEffect(() => {
    if (!ldClient || !userInfo.userId) return;

    const prev = prevUserInfoRef.current;
    const changed =
      prev.userId !== userInfo.userId ||
      prev.userEmail !== userInfo.userEmail ||
      prev.userName !== userInfo.userName ||
      prev.userGuid !== userInfo.userGuid ||
      prev.organizationId !== userInfo.organizationId ||
      prev.userType !== userInfo.userType;

    if (changed) {
      ldClient.identify(userContext);
      prevUserInfoRef.current = { ...userInfo };
    }
  }, [ldClient, userContext, userInfo]);

  return null;
}

export const LaunchDarklyProvider = ({
  children,
}: LaunchDarklyProviderProps) => {
  const [userInfo, setUserInfo] = useState<UserInfo>({});

  // Anonymous baseline context — used until a user logs in.
  const initialContext = useMemo<LaunchDarklyContext>(() => {
    const environment = process.env.NODE_ENV || 'development';
    const isProduction = environment === NODE_ENV.PRODUCTION;

    return {
      kind: 'user',
      key: `anonymous-user-${environment}`,
      anonymous: true,
      custom: {
        environment,
        isProduction,
        host:
          typeof window !== 'undefined' ? window.location.hostname : 'unknown',
        timestamp: new Date().toISOString(),
      },
    };
  }, []);

  const userContext = useMemo<LaunchDarklyContext>(() => {
    if (!userInfo.userId && !userInfo.userEmail) {
      return initialContext;
    }

    const environment = process.env.NODE_ENV || 'development';
    const isProduction = environment === NODE_ENV.PRODUCTION;

    return {
      kind: 'user',
      // This branch is only reached when a userId or email exists (see the
      // guard above), so the literal fallback is never actually used — it just
      // satisfies the `string` key type without an impure render-time call.
      key: userInfo.userId?.toString() || userInfo.userEmail || 'anonymous',
      anonymous: false,
      email: userInfo.userEmail,
      name: userInfo.userName,
      custom: {
        environment,
        isProduction,
        host:
          typeof window !== 'undefined' ? window.location.hostname : 'unknown',
        timestamp: new Date().toISOString(),
        userId: userInfo.userId,
        userGuid: userInfo.userGuid,
        organizationId: userInfo.organizationId,
        userType: userInfo.userType,
      },
    };
  }, [
    initialContext,
    userInfo.userId,
    userInfo.userEmail,
    userInfo.userName,
    userInfo.userGuid,
    userInfo.organizationId,
    userInfo.userType,
  ]);

  // LDProvider initialises the SDK in componentDidMount but renders children
  // immediately with empty flags — so the tree is stable from the very first
  // render. Previously we awaited waitUntilReady() and returned null until
  // then, which blocked SSR of the whole app and added ~7 s to LCP.
  const options = useMemo<LDClient.LDOptions>(
    () => ({
      bootstrap: 'localStorage',
      sendEvents: true,
      useReport: false,
      // Disable the SSE connection to clientstream.launchdarkly.com — it was
      // timing out in production and leaking ERR_TIMED_OUT errors into the
      // browser console (visible to PageSpeed Insights and end users). The
      // app does not need real-time flag updates; flags refresh on identify().
      streaming: false,
      // SDK default is 2 000 ms — with 6 components calling useFlags() the
      // queue fills quickly and the SDK fires ~9 POST requests to
      // events.launchdarkly.com in the first 18 s of a page load. 30 s is
      // plenty for analytics accuracy while cutting those flushes to at most 1.
      flushInterval: 30_000,
      inspectors: [
        {
          type: 'flag-used',
          name: 'dd-inspector',
          method: (key: string, detail: LDClient.LDEvaluationDetail) => {
            datadogRum.addFeatureFlagEvaluation(key, detail.value);
          },
        },
      ],
    }),
    [],
  );

  const updateUser = (newUserInfo: UserInfo) => {
    setUserInfo((prev) => ({ ...prev, ...newUserInfo }));
  };

  const getCurrentContext = () => userContext;

  return (
    <LaunchDarklyUpdateContext.Provider
      value={{ updateUser, getCurrentContext }}
    >
      <LDProvider
        clientSideID={process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID!}
        context={initialContext}
        options={options}
      >
        <LaunchDarklyIdentifySync
          userInfo={userInfo}
          userContext={userContext}
        />
        {children}
      </LDProvider>
    </LaunchDarklyUpdateContext.Provider>
  );
};
